import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/ExerciseDatabase.css';
import BaseLayout from './BaseLayout';
import { useAuth } from '../context/AuthContext';
import { buildApiUrl } from '../config/api';

function ExerciseDatabase() {
  const { token } = useAuth();
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const exercisesPerPage = 10;
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchExercises = async () => {
      try {
        const response = await fetch(buildApiUrl('/exercises'), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        const formattedExercises = data.map(exercise => ({
          name: exercise.name,
          category: exercise.category,
          youtubeLink: exercise.youtubeLink
        }));

        setExercises(formattedExercises);
        setLoading(false);
      } catch (error) {
        setError(error);
        setLoading(false);
      }
    };

    fetchExercises();
  }, [token]);

  const categories = ['All', ...new Set(exercises.map(exercise => exercise.category))].sort();
  const filteredExercises = exercises
    .filter(exercise => {
      const matchesCategory = selectedCategory === 'All' || exercise.category === selectedCategory;
      const matchesSearch = exercise.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          exercise.category.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesCategory && matchesSearch;
    });

  const indexOfLastExercise = currentPage * exercisesPerPage;
  const indexOfFirstExercise = indexOfLastExercise - exercisesPerPage;
  const currentExercises = filteredExercises.slice(indexOfFirstExercise, indexOfLastExercise);
  const totalPages = Math.ceil(filteredExercises.length / exercisesPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo(0, 0);
  };

  if (loading) return <BaseLayout><div className="loading-spinner">Loading exercises...</div></BaseLayout>;
  if (error) return (
    <BaseLayout>
      <div className="error-container">
        <h2>Error Loading Exercises</h2>
        <p>{error.message}</p>
        <button onClick={() => window.location.reload()}>Try Again</button>
      </div>
    </BaseLayout>
  );

  return (
    <BaseLayout>
      <div className="exercise-database">
        <h1>Exercise Database</h1>
        
        <div className="filter-section">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search exercises..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="search-input"
            />
          </div>

          <div className="category-filter">
            <label htmlFor="category-select">Filter by Category: </label>
            <select
              id="category-select"
              value={selectedCategory}
              onChange={(e) => {
                setSelectedCategory(e.target.value);
                setCurrentPage(1);
              }}
            >
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
          
          <span className="exercise-count">
            Showing {currentExercises.length} of {filteredExercises.length} exercises
          </span>
        </div>

        <div className="exercise-list">
          {currentExercises.length > 0 ? (
            <>
              <div className="exercise-header">
                <div className="exercise-column"><strong>Name</strong></div>
                <div className="exercise-column"><strong>Category</strong></div>
                <div className="exercise-column"><strong>YouTube Link</strong></div>
              </div>
              <ul>
                {currentExercises.map((exercise, index) => (
                  <li key={index} className="exercise-row">
                    <div className="exercise-column">{exercise.name}</div>
                    <div className="exercise-column">{exercise.category}</div>
                    <div className="exercise-column">
                      {exercise.youtubeLink && exercise.youtubeLink !== "0" ? (
                        <a 
                          href={exercise.youtubeLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="youtube-link"
                        >
                          Watch Video
                        </a>
                      ) : (
                        <span className="no-video">Not Available</span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <div className="no-exercises">No exercises found for this category.</div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <button 
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="pagination-arrow"
            >
              ←
            </button>
            
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((number) => (
              <button
                key={number}
                onClick={() => handlePageChange(number)}
                className={`pagination-number ${currentPage === number ? 'active' : ''}`}
              >
                {number}
              </button>
            ))}
            
            <button 
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="pagination-arrow"
            >
              →
            </button>
          </div>
        )}
      </div>
    </BaseLayout>
  );
}

export default ExerciseDatabase;